// Table-view flattening for document-collection results.
//
// A DOCUMENT collection SELECT always comes back as the two-column shape
// `_id, document`, where `document` is a JSON *string* (the server encodes it
// as `Value::Text`). In the raw table that renders as one long blob per row,
// which makes it hard to see what a projection actually returned — the whole
// point of `SELECT name, address.city`.
//
// B994: a KV collection is NOT that shape. It answers `key, value`, the same
// two columns `GET` answers, so it never matches the guard below and renders as
// the plain two-column table it already is. Nothing here needs a KV case.
//
// This expands that column ONE level: each top-level key of the document
// becomes its own table column. Nested objects/arrays are left as values for
// the cell renderer to show as compact JSON, so "flatten" here means exactly
// one level, never a deep `a.b.c` explosion.
//
// Pure and self-contained so it is unit-testable (`bun test`) without a DOM —
// same split as `use-metrics-utils.js`.

import { jsonSafeReplacer } from './cell-format.js';

/** The column whose JSON payload gets expanded. */
const DOC_COLUMN = 'document';
/** The storage-key column the server always emits alongside the document. */
const ID_COLUMN = '_id';

/**
 * Parse a cell that should hold a JSON object. Returns the object, or null if
 * the cell is not a JSON object (a plain string, an array, a scalar, malformed
 * JSON, …) — callers treat null as "not flattenable".
 */
function asJsonObject(cell) {
  if (cell && typeof cell === 'object' && !Array.isArray(cell)) return cell;
  if (typeof cell !== 'string') return null;
  const s = cell.trim();
  // Cheap reject before paying for a parse: an object literal must start `{`.
  if (!s.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(s);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Expand a `document` column into one column per top-level key.
 *
 * Returns a NEW `{ columns, data }`. Anything that is not a document-collection
 * result is returned UNCHANGED (same object identity), so ordinary table/ledger
 * results are untouched — a table may legitimately have a TEXT column named
 * `document`, and it must keep rendering as it always did.
 *
 * Key order is first-seen across all rows, so ragged documents (different
 * fields per row — normal in a schemaless collection) all get a column. The
 * client can do this union safely because it holds every row; the server
 * deliberately cannot, which is why the wire shape stays `_id, document`.
 *
 * Collisions: a document key that matches an existing column (notably `_id`,
 * which `SELECT *` carries both outside and inside the document) is dropped
 * when every row agrees, and otherwise kept under `document.<key>` so no value
 * is ever silently hidden.
 */
export function flattenDocumentColumn(result) {
  if (!result || result.type !== 'rows') return result;
  const { columns, data } = result;
  if (!Array.isArray(columns) || !Array.isArray(data) || data.length === 0) return result;

  const docIdx = columns.indexOf(DOC_COLUMN);
  if (docIdx === -1) return result;

  // Every row must carry a JSON object, else this is not a document result.
  const parsed = data.map(row => asJsonObject(row[docIdx]));
  if (parsed.some(p => p === null)) return result;

  // First-seen union of top-level keys.
  const keys = [];
  for (const doc of parsed) {
    for (const k of Object.keys(doc)) {
      if (!keys.includes(k)) keys.push(k);
    }
  }
  if (keys.length === 0) return result;

  const outer = columns.filter((_, i) => i !== docIdx);
  const nameFor = new Map();
  for (const k of keys) {
    if (!outer.includes(k)) {
      nameFor.set(k, k);
      continue;
    }
    // Collision with a real column: drop it only when it is genuinely the same
    // value wherever the document HAS it, otherwise qualify it rather than
    // hide it. Rows where the key is absent cannot contradict — a schemaless
    // collection is allowed to be ragged.
    const outerIdx = columns.indexOf(k);
    const identical = parsed.every((doc, r) => {
      if (!(k in doc)) return true;
      const inner = doc[k];
      const outerVal = data[r][outerIdx];
      // `jsonSafeReplacer` on both sides: a B607 cell can be a BigInt, and
      // plain `JSON.stringify` THROWS on one rather than comparing it.
      return inner === outerVal
        || JSON.stringify(inner, jsonSafeReplacer) === JSON.stringify(outerVal, jsonSafeReplacer);
    });
    nameFor.set(k, identical ? null : `${DOC_COLUMN}.${k}`);
  }

  const added = keys.filter(k => nameFor.get(k) !== null);

  // The row always ARRIVES as `_id, document` regardless of the projection, so
  // `SELECT name, _type` would otherwise show an `_id` column the user never
  // asked for. The document itself is the evidence: the server puts `_id` in it
  // only for `SELECT *` or an explicit `SELECT _id`. So when no document
  // carries `_id`, the outer column is projection noise and is hidden; the row
  // number still identifies the row, and the JSON view still shows the key.
  const dropOuterId = columns.includes(ID_COLUMN) && !parsed.some(doc => ID_COLUMN in doc);

  // One pass over the original columns: the document column expands in place,
  // a hidden `_id` is skipped, everything else keeps its position.
  const newColumns = [];
  const plan = [];
  for (let i = 0; i < columns.length; i++) {
    if (i === docIdx) {
      for (const k of added) {
        newColumns.push(nameFor.get(k));
        plan.push({ key: k });
      }
    } else if (dropOuterId && columns[i] === ID_COLUMN) {
      continue;
    } else {
      newColumns.push(columns[i]);
      plan.push({ index: i });
    }
  }
  const newData = data.map((row, r) => plan.map(p => (
    'index' in p
      ? row[p.index]
      // A field this document lacks becomes null — the collection is
      // schemaless, so absence is normal and not an error.
      : (p.key in parsed[r] ? parsed[r][p.key] : null)
  )));

  return { ...result, columns: newColumns, data: newData, flattened: true };
}
